<template>
  <el-card class="box-card">
    <div
        id="device-settings"
        slot="header"
        class="clearfix">
      <span class="header-item">Dynamic Settings</span>
      <span class="ring-card__header-buttons">

        <!-- v-if="isAdmin"-->
        <el-checkbox
            v-model="showOnlyAppSettings">Only App</el-checkbox>
        <el-divider direction="vertical" />
        <play-sound-test
            v-if="allowPlaySound"
            placement="left"
            @execute="(type) => executePlaySound(type)" />
        <ThroughputTest
            v-if="allowThroughputTest"
            style="display: inline-block"
            :device-kind="deviceKind"
            @execute="executeTest" />
      </span>
    </div>

    <div
        v-if="ready"
        v-loading="loading">
      <DynamicSettings
          :app-host="'ND'"
          :device="device"
          :json-schema="jsonSchema"
          :mobile-app="showOnlyAppSettings"
          :is-admin="isAdmin"
          @load-schema="fetchJsonSchema"
          @error="errorDynamicSettings"
          @update-setting="onUpdateDynamicSetting" />
    </div>
    <div
        v-else
        v-loading="loading"
        class="card-content"
        style="text-align: center;">
      <h1 v-if="loading">
        Loading Device Settings
      </h1>
      <div v-else>
        <h1>Problem loading settings</h1>
        <p>Reload the page, if the problem continue contact Admin</p>
      </div>
    </div>
  </el-card>
</template>

<script>
import axios from "axios";
import { isEmpty, get } from "lodash";
import RingProductSettings from "./Settings/RingProductSettings";
import ThroughputTest from "@/components/Shared/Device/Settings/ThroughputTest";
import PlaySoundTest from "@/components/Shared/Device/Settings/PlaySoundTest";
import ComputerVision from "@/components/Shared/Device/Settings/ComputerVision";

import DynamicSettings from "@cs_ventures/ring-device-settings-library/src/components/DynamicSettings";

import { createNamespacedHelpers } from "vuex";
const { mapGetters, mapActions } = createNamespacedHelpers("db/device");
import { mapGetters as mapSharedGetters } from "vuex";

export default {
  name: "DeviceSettings",
  components: {
    ThroughputTest,
    PlaySoundTest,
    ComputerVision,
    DynamicSettings,
    RingProductSettings,
  },
  data() {
    return {
      loading: false,
      jsonSchema: {},
      showOnlyAppSettings: true,
      deviceKind: "",
      deviceConnected: true,
    };
  },
  computed: {
    ...mapGetters(["settings", "type", "doorbotId", "macId", "device"]),
    ...mapSharedGetters({
      feature_flags: "shared/feature_flags",
    }),
    useV1SchemaRollback() {
      // Add null checks to handle undefined feature_flags in test environment
      if (!this.feature_flags || !this.feature_flags.features) {
        return false; // Default to using V3 schema if feature_flags is not available
      }

      // Find the feature flag by key and check if it exists and is active
      const flag = this.feature_flags.features.find(
        (f) => f.key === "useV1SchemaRollback"
      );
      return flag?.active === true;
    },
    isAdmin() {
      return this.$store.getters["auth/isAdmin"];
    },
    allowPlaySound() {
      const type = get(this, "type", "");
      return type.match(/chime/);
    },
    allowThroughputTest() {
      const type = get(this, "type", "");
      return !type.match(/chime/);
    },
    ready() {
      return !isEmpty(this.settings);
    },
  },
  created() {
    this.fetchJsonSchema();
  },
  beforeMount() {
    this.deviceKind = get(this, "type", "");
  },
  methods: {
    ...mapActions(["updateDevice", "execThroughputTest", "executeTestSound"]),
    fetchJsonSchema() {
      this.loading = true;
      const endpoint = this.useV1SchemaRollback
        ? `/v2/settings/schema.json?type=${this.type}`
        : `/v3/settings/schema?type=${this.type}`;
      console.log(`Fetching schema from: ${endpoint}`);
      axios
        .get(endpoint)
        .then((response) => {
          this.jsonSchema = response.data;
        })
        .catch(() => {
          this.jsonSchema = {};
          console.info("No Schema Registry");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onUpdateSetting({ payload, done }) {
      this.loading = true;
      this.updateDevice({ doorbotId: this.doorbotId, payload })
        .then(this.onSuccess("Setting updated", done))
        .catch(this.onError("Setting Update", "Could not update setting"))
        .finally(() => (this.loading = false));
    },
    onUpdateDynamicSetting({ payload, callback }) {
      if (typeof payload.meta === "object") {
        this.updateComplexSetting({ payload, callback });
      } else {
        this.updateSimpleSetting({ payload, callback });
      }
    },
    errorDynamicSettings(error) {
      console.log("Error With Dynamic Settings", error);
      this.onError("Error With Dynamic Settings", "Could not update setting");
    },
    updateSimpleSetting({ payload, callback }) {
      this.loading = true;
      this.updateDevice({ doorbotId: this.doorbotId, payload })
        .then(this.onSuccess("Setting updated", callback))
        .catch(
          this.onError("Setting Update", "Could not update setting", callback)
        )
        .finally(() => (this.loading = false));
    },
    updateComplexSetting({ payload, callback }) {
      const meta = payload.meta;
      delete payload.meta;
      delete payload.doorbotId;

      const done = (response) => {
        this.onSuccess("Setting updated", callback)(response);
        meta.done(response);
      };

      const failure = (error) => {
        this.onError(
          "Setting Update",
          "Could not update setting",
          callback
        )(error);
        meta.failure(error);
      };

      this.loading = true;
      axios({ method: meta.verb, url: meta.url, data: payload })
        .then(done)
        .catch(failure)
        .finally(() => (this.loading = false));
    },
    executePlaySound(type, done) {
      this.executeTestSound({
        id: this.doorbotId,
        device_kind: this.type,
        event_type: type,
      })
        .then(this.onSuccess("Test executed"))
        .catch(
          this.onError(
            "Play Sound test",
            `Could not execute the test for ${type}`
          )
        )
        .finally(() => done());
    },
    executeTest(test_type, done) {
      if (localStorage.getItem("vuex")) {
        const vuex = JSON.parse(localStorage.getItem("vuex"));
        this.deviceConnected = vuex["db"]["device"]["device"]["connected"];
      }
      if (this.deviceConnected) {
        this.execThroughputTest({
          device_id: this.doorbotId,
          device_kind: this.type,
          test_type,
        })
          .then(this.onSuccess("Test executed"))
          .catch(this.onError("Throughput Test", "Could not execute Test"))
          .finally(() => done());
      } else {
        const h = this.$createElement;
        this.$notify({
          title: "Throughput test",
          message: h(
            "i",
            { class: { "update-setting_error_msg": true } },
            "Unable to perform throughput test on offline devices"
          ),
          duration: 3000,
        });
        done();
      }
    },
    onSuccess(msg, cb) {
      return (response) => {
        if (cb) cb(this.device);
        this.$message({ showClose: true, message: msg, type: "success" });
      };
    },
    onError(errorTitle, msg, cb) {
      return (err) => {
        console.error(err);
        if (cb) cb(err, false);
        const h = this.$createElement;
        const serverMessage = get(err, "data.error", null) || msg;
        this.$notify({
          title: errorTitle,
          message: h(
            "i",
            { class: { "update-setting_error_msg": true } },
            serverMessage
          ),
          duration: 3000,
        });
      };
    },
    hasAdditionalSettings() {
      return !this.type.match(/intercom_handset_audio/);
    },
  },
};
</script>

<style lang="scss">
.device-settings__container {
  h1,
  h2,
  h3,
  h4,
  h5 {
    margin-left: 16px;
  }
  h3,
  h4,
  h5 {
    color: $--color-dusty-gray;
  }
  .el-col {
    min-height: max-content;
    height: 700px;
    padding-left: 10px;
    padding-right: 10px;
    border-right: thin solid $--color-silver;
  }
}
.update-setting_error_msg {
  color: $--color-milano-red;
  font-size: smaller;
  font-style: normal;
}
</style>
